/**
 * Research progress, from either wire — and the one judgement of when an
 * address's research has just FINISHED.
 *
 * Progress reaches the dashboard two ways: the research stream (one push per
 * change of a request, when `seed-mgmt-api`'s publisher is deployed) and the
 * list/get poll (`@/api/researchSource`), which stays as the fallback. Both are
 * read into one {@link ResearchUpdate}, and {@link ResearchProgressTracker}
 * decides — once, for whichever wire saw it first — which subjects just went
 * done, so exactly those addresses' queries are refreshed
 * (`@/api/invalidateAddressQueries`). A second observer of the same transition
 * (the poll after the push, two hosts listing one request) sees no transition.
 *
 * "Just finished" needs a BEFORE. A poll's first sight of a request finished an
 * hour ago is not a transition — refreshing every address of every old job on
 * each page load is the refetch storm this exists to avoid. A push is a change
 * by construction, so a pushed subject seen done for the first time is one.
 */
import { normalizeAddress } from "@authe/fe-commons/chains";
import { researchRequestFromWire, type ResearchRequest, type ResearchStatus } from "@/api/researchSource";

/** One subject's progress, as either wire states it. */
export interface ResearchUpdateSubject {
  chainId: string;
  address: string;
  /** The run's own status (`queued` / `running` / `succeeded` / `failed` / `cancelled`), or `null`. */
  runStatus: string | null;
  /**
   * The server's own "this address is finished" (`finished`), or `null` from a
   * wire that predates it — then the run status decides.
   */
  finished: boolean | null;
  recordsWritten: number;
  recordsReviewQueued: number;
  forensicClaimsWritten: number;
}

/** A request's progress — the stream envelope, or a polled request read down to it. */
export interface ResearchUpdate {
  requestId: string;
  /** `null` when the wire did not say. */
  status: ResearchStatus | null;
  /** What the request works for — `{kind, id}`; a personal one may carry no id. */
  context: { kind: string; id: string | null } | null;
  subjects: ResearchUpdateSubject[];
  /**
   * The push read as a whole request (it carries the GET shape: counts,
   * audience, profile, every subject's counts) — `null` from a poll's own
   * update or a push too thin to be one. Hosts merge it over what they list,
   * keeping what a push never carries (each address's forensic record).
   */
  request: ResearchRequest | null;
}

/** An address whose research just finished — what an invalidation is keyed by. */
export interface ResearchSubjectRef {
  chainId: string;
  address: string;
}

const STATUSES: readonly ResearchStatus[] = ["running", "done", "partial", "failed", "not_started"];
const TERMINAL_RUN = new Set(["succeeded", "failed", "cancelled"]);
const NOT_STARTED_OUTCOMES = new Set(["over_budget", "disabled", "failed"]);
const TERMINAL_REQUEST = new Set<ResearchStatus>(["done", "partial", "failed", "not_started"]);

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function count(value: unknown): number {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** A polled request, read down to its progress. */
export function researchUpdateFromRequest(request: ResearchRequest): ResearchUpdate {
  return {
    requestId: request.requestId,
    status: request.status,
    context: request.context ? { kind: request.context.kind, id: request.context.id || null } : null,
    // An address that never got a run (over budget, research off) has nothing
    // to finish and nothing to refresh.
    subjects: request.subjects.filter((sub) => sub.runId).map((sub) => ({
      chainId: sub.chainId,
      address: sub.address,
      runStatus: sub.runStatus,
      finished: sub.finished ?? null,
      recordsWritten: sub.recordsWritten,
      recordsReviewQueued: sub.recordsReviewQueued,
      forensicClaimsWritten:
        sub.forensicClaimsWritten ??
        sub.forensics?.findings.reduce((n, f) => n + f.claims.filter((c) => c.outcome === "written").length, 0) ??
        0,
    })),
    request: null,
  };
}

/**
 * The research stream's envelope, read and checked, or `null` when the payload
 * is not one.
 *
 * `seed-mgmt-api`'s publisher sends it FLAT: `{stream: "research", id,
 * request_id, status, context, audience, profile, counts, updated_at, subjects:
 * [{chain_id, address, outcome, run_status, finished, records_written, …}]}` —
 * `id` is the request id, so a re-delivery (blue and green both publishing, a
 * retry) collapses in the client's dedupe. Also read as the `header` or
 * `request` of a wrapping envelope, should one ever carry it. Tolerant the way
 * the REST reader is: an unknown status is `null`, a subject without an address
 * is dropped, a count that is not a number is zero. Never throws.
 */
export function readResearchEvent(payload: unknown): ResearchUpdate | null {
  const outer = record(payload);
  if (!outer) return null;
  const candidates = [outer, record(outer.header), record(outer.request)];
  const body = candidates.find((c) => c && str(c.request_id));
  if (!body) return null;
  const requestId = str(body.request_id)!;
  const status = STATUSES.includes(body.status as ResearchStatus) ? (body.status as ResearchStatus) : null;
  const ctx = record(body.context);
  const subjects = (Array.isArray(body.subjects) ? body.subjects : []).flatMap((raw): ResearchUpdateSubject[] => {
    const sub = record(raw);
    const address = sub ? str(sub.address) : null;
    const chainId = sub ? str(sub.chain_id) : null;
    if (!sub || !address || !chainId) return [];
    // Never started (over budget, research off, refused): nothing to finish.
    if (NOT_STARTED_OUTCOMES.has(str(sub.outcome) ?? "")) return [];
    return [
      {
        chainId,
        address,
        runStatus: str(sub.run_status),
        finished: typeof sub.finished === "boolean" ? sub.finished : null,
        recordsWritten: count(sub.records_written),
        recordsReviewQueued: count(sub.records_review_queued),
        forensicClaimsWritten: count(sub.forensic_claims_written),
      },
    ];
  });
  return {
    requestId,
    status,
    context: ctx && str(ctx.kind) ? { kind: str(ctx.kind)!, id: str(ctx.id) } : null,
    subjects,
    // The flat research envelope is the GET shape plus `stream`/`id`; one with
    // its counts can stand in for the request.
    request: status && record(body.counts) ? researchRequestFromWire(body) : null,
  };
}

/** Whether a finished subject can have changed what any surface shows. */
function wroteOrSucceeded(sub: ResearchUpdateSubject): boolean {
  return (
    sub.runStatus === "succeeded" ||
    sub.recordsWritten > 0 ||
    sub.recordsReviewQueued > 0 ||
    sub.forensicClaimsWritten > 0
  );
}

interface TrackedRequest {
  status: ResearchStatus | null;
  observedAt: number;
  /** Started from this tab — its first poll is a "before", not old news. */
  tracked: boolean;
}

/** Bound on remembered subjects; the oldest are forgotten first. */
const MAX_SUBJECTS = 5_000;

/**
 * Remembers whether each (request, subject) was done when last seen, and says
 * which subjects an update moved to done. One per signed-in identity — the
 * dashboard's `ResearchLiveProvider` holds it; a host outside one keeps its own.
 */
export class ResearchProgressTracker {
  private readonly subjects = new Map<string, boolean>();
  private readonly requests = new Map<string, TrackedRequest>();

  /**
   * Register a request this tab just created, so its completion refreshes the
   * addresses wherever the reader has gone since.
   */
  track(update: ResearchUpdate, now: number = Date.now()): void {
    this.requests.set(update.requestId, { status: update.status, observedAt: now, tracked: true });
    const finished = update.status !== null && TERMINAL_REQUEST.has(update.status);
    for (const sub of update.subjects) {
      this.remember(this.key(update.requestId, sub), isSubjectDone(sub, finished));
    }
  }

  /**
   * Record an update and return the subjects it moved to done — each once.
   *
   * A subject seen before and not done then is a transition. One seen for the
   * first time is a transition only when the update is a push (`"stream"`: a
   * push is a change) or the request was started here or seen running before;
   * a poll's first sight of anything else is a baseline.
   */
  observe(update: ResearchUpdate, source: "stream" | "poll", now: number = Date.now()): ResearchSubjectRef[] {
    const previous = this.requests.get(update.requestId);
    const wasActive =
      previous !== undefined && (previous.tracked || previous.status === "running" || previous.status === null);
    const status = update.status ?? previous?.status ?? null;
    this.requests.set(update.requestId, { status, observedAt: now, tracked: previous?.tracked ?? false });
    const finished = status !== null && TERMINAL_REQUEST.has(status);

    const done: ResearchSubjectRef[] = [];
    for (const sub of update.subjects) {
      const key = this.key(update.requestId, sub);
      const before = this.subjects.get(key);
      const isDone = isSubjectDone(sub, finished);
      this.remember(key, isDone);
      if (!isDone || before === true) continue;
      const transition = before === false || source === "stream" || wasActive;
      // A run that failed and wrote nothing changed nothing anyone sees.
      if (transition && (!sub.runStatus || wroteOrSucceeded(sub))) {
        done.push({ chainId: sub.chainId, address: sub.address });
      }
    }
    return done;
  }

  /**
   * Requests started here (or seen running) that have not finished and have
   * not been heard from within `staleAfterMs` — the ones a background poll
   * asks about while no push is arriving.
   */
  staleActive(staleAfterMs: number, now: number = Date.now()): string[] {
    const out: string[] = [];
    for (const [id, req] of this.requests) {
      const active = req.status === null || req.status === "running";
      if (active && (req.tracked || req.status === "running") && now - req.observedAt >= staleAfterMs) out.push(id);
    }
    return out;
  }

  /** Whether this tab started the request ({@link track}). */
  isTracked(requestId: string): boolean {
    return this.requests.get(requestId)?.tracked ?? false;
  }

  private key(requestId: string, sub: Pick<ResearchUpdateSubject, "chainId" | "address">): string {
    return `${requestId}|${sub.chainId}|${normalizeAddress(sub.address, sub.chainId)}`;
  }

  private remember(key: string, done: boolean): void {
    // Re-inserted so the map's order is recency, and the bound drops the oldest.
    this.subjects.delete(key);
    this.subjects.set(key, done);
    while (this.subjects.size > MAX_SUBJECTS) {
      const oldest = this.subjects.keys().next().value;
      if (oldest === undefined) break;
      this.subjects.delete(oldest);
    }
  }
}

/**
 * A subject is done when the server says it is (`finished`) — or, for a wire
 * that predates the flag, when its run is, or (no run status either) when the
 * request as a whole finished.
 */
function isSubjectDone(sub: ResearchUpdateSubject, requestFinished: boolean): boolean {
  if (sub.finished !== null) return sub.finished;
  return sub.runStatus ? TERMINAL_RUN.has(sub.runStatus) : requestFinished;
}
